import { useState, useRef, useEffect } from "react";
import { useImmer } from "use-immer";
import SimpleReactValidator from "simple-react-validator";
import {
  getTenantLinkSettingsApi,
  updateTenantLinkSettingsApi,
} from "../apis/linkApis";
import { errorMessage, successMessage } from "../../../utils";

export const useLinkConfig = () => {
  const [_, setForceUpdate] = useState(0);
  const [state, setState] = useImmer({
    formData: {
      privacy_policy: "",
      terms_of_service: "",
      refund_policy: "",
      help_center: "",
    },
    isLoading: true,
    isSubmitting: false,
    hasChanges: false,
  });

  const formValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: setForceUpdate },
    }),
  );

  //   Fetch the initial link configuration when the component mounts
  useEffect(() => {
    fetchLinks();
  }, []);

  //   Fetch the tenant link settings from the API
  const fetchLinks = async () => {
    try {
      triggerLoading(true);

      const response = await getTenantLinkSettingsApi();

      if (response?.success) {
        setState((draft) => {
          draft.formData = response.data;
        });
      } else {
        throw new Error(
          response.message || "Failed to fetch tenant link settings",
        );
      }
    } catch (error) {
      const erMessage =
        error.response?.data?.message ||
        error?.message ||
        "Failed to fetch tenant link settings";
      errorMessage(erMessage);
    } finally {
      triggerLoading(false);
    }
  };

  //   Update the tenant link settings
  const updateLinks = async (payload) => {
    try {
      triggerSubmitting(true);

      const response = await updateTenantLinkSettingsApi(payload);

      if (response?.success) {
        setState((draft) => {
          draft.formData = response.data;
          draft.hasChanges = false;
        });
        successMessage(response.message);
        fetchLinks();
      } else {
        throw new Error(
          response.message || "Failed to update tenant link settings",
        );
      }
    } catch (error) {
      const erMessage =
        error.response?.data?.message ||
        error?.message ||
        "Failed to update tenant link settings";
      errorMessage(erMessage);
    } finally {
      triggerSubmitting(false);
    }
  };

  const triggerLoading = (loading) => {
    setState((draft) => {
      draft.isLoading = loading;
    });
  };

  const triggerSubmitting = (submitting) => {
    setState((draft) => {
      draft.isSubmitting = submitting;
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setState((draft) => {
      draft.formData[name] = value;
      if (draft.hasChanges === false) draft.hasChanges = true;
    });
  };

  const onSubmit = () => {
    if (formValidator.current.allValid()) {
      // Submit the form data
      updateLinks(state.formData);
    } else {
      formValidator.current.showMessages();
      setForceUpdate((prev) => prev + 1);
    }
  };

  return { state, formValidator, setState, onChange, onSubmit };
};
